/**
 * Zustand store for referral management.
 * Manages referral slug, dashboard data, and related state.
 */
import { create } from "zustand";
import {
  getReferralSlug,
  updateReferralSlug,
  checkSlugAvailability,
  getReferralDashboard,
  type ReferralSlugData,
  type ReferralDashboardResponse,
  type SlugAvailability,
} from "@/utils/api/referrals";

interface ReferralStore {
  // Slug management
  slugData: ReferralSlugData | null;
  isLoadingSlug: boolean;
  isEditingSlug: boolean;
  customSlugInput: string;
  slugCheckMessage: string;
  isSlugAvailable: boolean | null;
  isCheckingSlug: boolean;
  isSavingSlug: boolean;

  // Dashboard data
  dashboard: ReferralDashboardResponse | null;
  isLoadingDashboard: boolean;
  dashboardError: Error | null;
  currentPage: number;
  pageSize: number;

  // History table state
  expandedRows: Set<string>;

  // Actions - Slug management
  loadSlug: () => Promise<void>;
  setIsEditingSlug: (isEditing: boolean) => void;
  setCustomSlugInput: (input: string) => void;
  checkSlugAvailabilityAsync: (slug: string) => Promise<void>;
  saveSlug: (slug: string | null) => Promise<void>;
  cancelEdit: () => void;
  resetSlugCheckState: () => void;

  // Actions - Dashboard
  loadDashboard: (page?: number) => Promise<void>;
  setCurrentPage: (page: number) => void;
  refetchDashboard: () => Promise<void>;

  // Actions - History table
  toggleRowExpansion: (rowId: string) => void;
}

export const useReferralStore = create<ReferralStore>((set, get) => ({
  // Initial state - Slug management
  slugData: null,
  isLoadingSlug: false,
  isEditingSlug: false,
  customSlugInput: "",
  slugCheckMessage: "",
  isSlugAvailable: null,
  isCheckingSlug: false,
  isSavingSlug: false,

  // Initial state - Dashboard
  dashboard: null,
  isLoadingDashboard: false,
  dashboardError: null,
  currentPage: 1,
  pageSize: 20,

  // Initial state - History table
  expandedRows: new Set(),

  // Actions - Slug management
  loadSlug: async () => {
    set({ isLoadingSlug: true });
    try {
      const data = await getReferralSlug();
      set({
        slugData: data,
        customSlugInput: data.referralSlug || "",
      });
    } catch (error) {
      console.error("Error loading slug:", error);
    } finally {
      set({ isLoadingSlug: false });
    }
  },

  setIsEditingSlug: (isEditing: boolean) => {
    const { slugData } = get();
    set({
      isEditingSlug: isEditing,
      // Initialize input with current slug when entering edit mode
      customSlugInput: isEditing
        ? slugData?.referralSlug || ""
        : get().customSlugInput,
    });
    if (!isEditing) {
      // Reset check state when exiting edit mode
      get().resetSlugCheckState();
    }
  },

  setCustomSlugInput: (input: string) => {
    set({ customSlugInput: input.toLowerCase() });
  },

  checkSlugAvailabilityAsync: async (slug: string) => {
    if (!slug.trim()) {
      get().resetSlugCheckState();
      return;
    }

    // Skip check if it's the current slug
    const { slugData } = get();
    if (slug === slugData?.referralSlug) {
      get().resetSlugCheckState();
      return;
    }

    set({ isCheckingSlug: true });
    try {
      const result: SlugAvailability = await checkSlugAvailability(slug);
      set({
        isSlugAvailable: result.available,
        slugCheckMessage: result.message,
      });
    } catch (error) {
      console.error("Error checking slug:", error);
      set({
        slugCheckMessage: "Error checking availability",
        isSlugAvailable: false,
      });
    } finally {
      set({ isCheckingSlug: false });
    }
  },

  saveSlug: async (slug: string | null) => {
    set({ isSavingSlug: true });
    try {
      const data = await updateReferralSlug(slug);
      set({
        slugData: data,
        customSlugInput: data.referralSlug || "",
        isEditingSlug: false,
      });
      get().resetSlugCheckState();

      // Refetch dashboard to update the referral link
      await get().refetchDashboard();
    } catch (error) {
      console.error("Error saving slug:", error);
      throw error;
    } finally {
      set({ isSavingSlug: false });
    }
  },

  cancelEdit: () => {
    const { slugData } = get();
    set({
      customSlugInput: slugData?.referralSlug || "",
      isEditingSlug: false,
    });
    get().resetSlugCheckState();
  },

  resetSlugCheckState: () => {
    set({
      slugCheckMessage: "",
      isSlugAvailable: null,
      isCheckingSlug: false,
    });
  },

  // Actions - Dashboard
  loadDashboard: async (page?: number) => {
    const pageToLoad = page ?? get().currentPage;
    const { pageSize } = get();

    set({ isLoadingDashboard: true, dashboardError: null });
    try {
      const data = await getReferralDashboard(pageToLoad, pageSize);
      set({
        dashboard: data,
        currentPage: pageToLoad,
      });
    } catch (error) {
      console.error("Error loading dashboard:", error);
      set({ dashboardError: error as Error });
    } finally {
      set({ isLoadingDashboard: false });
    }
  },

  setCurrentPage: (page: number) => {
    set({ currentPage: page });
    get().loadDashboard(page);
  },

  refetchDashboard: async () => {
    const { currentPage } = get();
    await get().loadDashboard(currentPage);
  },

  // Actions - History table
  toggleRowExpansion: (rowId: string) => {
    const { expandedRows } = get();
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(rowId)) {
      newExpanded.delete(rowId);
    } else {
      newExpanded.add(rowId);
    }
    set({ expandedRows: newExpanded });
  },
}));
